import { DocumentStatesEnum } from '../enums/document-states.enum';
import { DocumentTypesEnum } from '../enums/document-types.enum';
import { VisaStatesEnum } from '../enums/visa-states.enum';
import { RawDocumentInterface } from '../interfaces/raw-document.interface';
import { DocumentVisa } from './dcoument-visa.model';
import { Tag } from './tag.model';
import { Colors } from '../enums/colors.enum';
import moment from 'moment';
import { BydValidation } from './byd-validation.model';
import { DOCUMENT_FILE_FORMAT } from '../constants/document-size-formats.constant';
import { DocumentValidation } from './document-validation.model';
import { ValidationStateEnum } from '../enums/validation-states.enum';

export class Document {
  /**
   * Id.
   */
  id: number;

  /**
   * Name.
   */
  name: string;

  /**
   * History count.
   */
  historyCount = 'v1';

  /**
   * Created by.
   */
  createdBy: string;

  /**
   * Created at.
   */
  createdAt: Date;

  /**
   * Updated at.
   */
  updatedAt: Date;

  /**
   * tags.
   */
  tags: Tag[];

  /**
   * DocumentTypeEnum
   */
  type: DocumentTypesEnum;

  /**
   * Parent id.
   */
  parentId?: number;

  /**
   * Array of childrens.
   */
  children?: Document[];

  /**
   * File link to download.
   */
  file?: string;

  /**
   * State.
   */
  state: string;

  /**
   * Visas.
   */
  visas?: DocumentVisa[];

  /**
   * File extinsion.
   */
  mime?: string;

  /**
   * Validations for ds-lib-table.
   */
  validations: BydValidation[] = [];

  /**
   * Document size.
   */
  size = '0 Ko';

  /**
   * Is the folder don't have any folder as children.
   */
  emptyFromFolders: boolean;

  /**
   * ModelId.
   */
  modelId?: number;

  /**
   * Document constructor.
   *
   * @param document Raw document.
   */
  constructor(document: RawDocumentInterface) {
    this.id = document.id;
    this.name = document.name;
    this.historyCount = document.history_count ? `v${document.history_count + 1}` : this.historyCount;
    this.createdBy = document.created_by ? `${document.created_by.firstname} ${document.created_by.lastname}` : '';
    this.createdAt = new Date(document.created_at);
    this.updatedAt = new Date(document.updated_at);
    this.tags = document.tags?.map((tag: Tag) => new Tag(tag));
    this.type = document.type;
    this.parentId = document.parent_id;
    this.size = document.size ? this.formatSize(document.size) : this.size;
    this.file = document.file;
    const ext = document.file_name?.split('.').pop();
    this.mime = ext === document.file_name ? 'unknown' : ext?.toLowerCase();
    this.modelId = document.model_id;
    this.children = document.children
      ?.map((doc: RawDocumentInterface) => new Document(doc))
      .sort((doc1: Document, doc2: Document) => doc2.createdAt.getTime() - doc1.createdAt.getTime());
    if (document.type === DocumentTypesEnum.FOLDER) {
      this.emptyFromFolders =
        this.children?.filter((doc: Document) => doc.type === DocumentTypesEnum.FOLDER)?.length < 1;
    }
  }

  /**
   * Find folder.
   *
   * @param folderId FolderId
   * @returns folder or null.
   */
  findFolder(folderId: number) {
    const stack: Document[] = [this];
    while (stack.length) {
      const node = stack.shift();
      if (node?.id === folderId) return node;
      if (node?.children) stack.push(...node.children);
    }
    return null;
  }

  /**
   * Get all documents.
   *
   * @returns DOcument[]
   */
  public getAllDocuments(): Document[] {
    const stack: Document[] = [this];
    const documents: Document[] = [];
    while (stack.length) {
      const node = stack.shift();
      if (node && (node.type === DocumentTypesEnum.DOCUMENT || node.type === DocumentTypesEnum.PLAN))
        documents.push(node);
      if (node?.children) stack.push(...node.children);
    }
    return documents.sort((doc1: Document, doc2: Document) => doc2.createdAt.getTime() - doc1.createdAt.getTime());
  }

  /**
   * Get direct documents of current folder.
   *
   * @returns array of documents.
   */
  public getDirectDocuments(): Document[] {
    return (
      this.children?.filter(
        (document: Document) => document.type === DocumentTypesEnum.DOCUMENT || document.type === DocumentTypesEnum.PLAN
      ) || []
    );
  }

  /**
   * Set state.
   *
   */
  public updateStateAndValidations(): void {
    if (!this.visas?.length) {
      this.state = DocumentStatesEnum.WITHOUT_VALIDATION;
      this.validations = [];
      return;
    }

    let state = DocumentStatesEnum.VALIDATED;
    let color: string;
    let stateVisa: string;
    let comment: string;
    let responseDate: Date | null;
    let validator: string;
    let validation;
    let dueDate: number;
    let nbOfDays: number;
    let attachment: string;
    this.visas?.forEach((visa: DocumentVisa) => {
      validation = visa?.validations?.find((v: DocumentValidation) => v.state !== ValidationStateEnum.PENDING);
      dueDate = visa.deadline * 1000;
      nbOfDays = this.CalculateNbDays(dueDate);
      if (validation) {
        responseDate = validation?.creationDate;
        validator = `${validation.validator?.firstname} ${validation?.validator?.lastname}`;
        attachment = validation?.attachment;
      } else {
        responseDate = null;
        validator = '';
      }
      if (visa.state === VisaStatesEnum.OPEN) {
        color = Colors.gray;
        if (
          visa.deadline <
          moment()
            .startOf('day')
            .unix()
        )
          state = DocumentStatesEnum.DELAYED;
        else if (state !== DocumentStatesEnum.DELAYED) state = DocumentStatesEnum.WAITING;
      } else {
        if (this.isValidJSON(visa?.comments[0]?.content)) {
          color = JSON.parse(visa?.comments[0]?.content)?.color || Colors.stripes;
          comment = JSON.parse(visa?.comments[0]?.content)?.comment;
          stateVisa = JSON.parse(visa?.comments[0]?.content)?.state;
        } else {
          color = Colors.stripes;
          stateVisa = 'unassigned';
          comment = '';
        }
      }
      const valdidation = new BydValidation({
        label: visa.description,
        isExpired: state === DocumentStatesEnum.DELAYED,
        color,
        stateVisa,
        comment,
        responseDate,
        validator,
        dueDate,
        nbOfDays,
        attachment,
      });
      this.validations.push(valdidation);
    });
    this.state = state;
  }

  /**
   * Format Size.
   *
   * @param size number.
   * @returns string.
   */
  public formatSize(size: number): string {
    const absSize = Math.abs(size);
    for (let i = 0; i < DOCUMENT_FILE_FORMAT.length; i++) {
      if (absSize < <number>DOCUMENT_FILE_FORMAT[i][0])
        return Math.ceil(absSize / <number>DOCUMENT_FILE_FORMAT[i - 1][0]) + ' ' + DOCUMENT_FILE_FORMAT[i - 1][1];
    }
    return this.size;
  }

  /**
   * Returns number of days.
   *
   * @param dueDate - columns of the table to translate.
   * @returns {number} return number of days.
   */
  private CalculateNbDays(dueDate: number) {
    const currentDateMillis = new Date().getTime();
    const differenceMillis = currentDateMillis - dueDate;
    const nbOfDays = Math.ceil(differenceMillis / (1000 * 60 * 60 * 24)) - 1;
    return nbOfDays >= 0 ? nbOfDays : 0;
  }

  /**
   * Is valid json.
   *
   * @param {string} jsonString - The content json.
   * @returns {boolean} response.
   */
  public isValidJSON(jsonString: string): boolean {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (error) {
      return false;
    }
  }
}
